'use client'

import {
  alpha,
  Box,
  ListItemText,
  MenuItem,
  MenuList,
  Typography,
} from '@mui/material'
import { CheckIcon } from 'icons'
import { useRef, useState, type KeyboardEvent } from 'react'
import { FilterDropdown } from '../FilterDropdown'
import { SearchField } from '../SearchField'

const ENABLED_ROW = '[role="menuitemradio"]:not([aria-disabled="true"])'

export interface FilterSelectOption {
  value: string
  label: string
  // A second line beneath the label, for an option whose label needs qualifying (e.g. the date
  // range a time period covers).
  subLabel?: string
  // Results the option leads to, shown right-aligned; a 0 disables it unless it's the current value.
  count?: number
}

// The row shape shared by the options and the optional cleared-state row.
interface RowContent {
  label: string
  subLabel?: string
  count?: number
}

// A single-select filter dropdown. With `searchable`, the panel opens with a
// search input that narrows the option list. Picking an option closes the panel;
// the active chip's X clears the selection (the panel's redundant "Clear" link
// is omitted).
export function FilterSelect({
  label,
  summaryPrefix,
  formatSummary,
  options,
  value,
  onChange,
  nullOption,
  showSelectedCheck = false,
  searchable = false,
  searchPlaceholder = 'Search…',
  fullWidth = false,
  highlightSelected = true,
  clearable = true,
  disabled = false,
}: {
  label: string
  // Chip text when active is `${summaryPrefix}: ${selected label}`. Optional
  // when `formatSummary` is supplied, which then owns the chip text entirely.
  summaryPrefix?: string
  // Derives the active chip text from the selected option, letting the chip
  // show a short form (e.g. "Lesson 1") while the option list keeps the full
  // label (e.g. "Lesson 1: Intro").
  formatSummary?: (option: FilterSelectOption) => string
  options: FilterSelectOption[]
  value: string | null
  onChange: (value: string | null) => void
  // An explicit row for the cleared state, listed first — it's the widest choice
  // and the one a user returns to, so it reads as the top of the list rather
  // than a footnote. For a selector whose default is itself a real choice ("All
  // time"), so it can be highlighted and checked in the list rather than living
  // only as the trigger's placeholder. Picking it clears the value.
  nullOption?: RowContent
  // Marks the selected row with a checkmark as well as the wash.
  showSelectedCheck?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  // Stretches the trigger to fill its container, pushing label and caret/clear
  // to opposite edges — a full-width field rather than a content-sized pill.
  fullWidth?: boolean
  // When false, the trigger chip stays neutral once a value is picked (no
  // primary wash/border) — for selectors where the choice isn't a "filter is
  // active" signal. The selected option row is highlighted regardless.
  highlightSelected?: boolean
  // When false, a picked value can be changed but not cleared (e.g. a sort order).
  clearable?: boolean
  disabled?: boolean
}) {
  const [query, setQuery] = useState('')
  const listRef = useRef<HTMLUListElement>(null)

  const selected = options.find((option) => option.value === value) ?? null
  const summary = selected
    ? (formatSummary?.(selected) ??
      (summaryPrefix ? `${summaryPrefix}: ${selected.label}` : selected.label))
    : null
  const matchesQuery = (text: string) =>
    text.toLowerCase().includes(query.toLowerCase().trim())
  const visibleOptions = query
    ? options.filter((option) => matchesQuery(option.label))
    : options
  const visibleNullOption =
    nullOption && (!query || matchesQuery(nullOption.label)) ? nullOption : null
  const visibleCount = visibleOptions.length + (visibleNullOption ? 1 : 0)
  const isOptionDisabled = (option: FilterSelectOption) =>
    option.count === 0 && option.value !== value

  return (
    <FilterDropdown
      label={label}
      summary={summary}
      fullWidth={fullWidth}
      highlightSelected={highlightSelected}
      onClear={
        clearable
          ? () => {
              onChange(null)
            }
          : undefined
      }
      showClearLink={false}
      disabled={disabled}
    >
      {(close, { openedWithKeyboard }) => {
        const pick = (next: string | null) => {
          onChange(next)
          setQuery('')
          close()
        }

        const handleSearchKeyDown = (
          event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            listRef.current?.querySelector<HTMLElement>(ENABLED_ROW)?.focus()
            return
          }
          // The Enter that confirms an IME composition is not a pick.
          if (event.nativeEvent.isComposing) return
          if (event.key !== 'Enter' || visibleCount !== 1) return
          event.preventDefault()
          // Unlike a row click, Enter never clears: the user just typed the name.
          if (visibleNullOption) {
            pick(null)
          } else if (!isOptionDisabled(visibleOptions[0])) {
            pick(visibleOptions[0].value)
          }
        }

        return (
          <>
            {searchable ? (
              <Box sx={{ px: 2, py: 1 }}>
                <SearchField
                  value={query}
                  onChange={setQuery}
                  onKeyDown={handleSearchKeyDown}
                  // Typing narrows straight away; ArrowDown reaches the list.
                  // Keyboard opens only: on a tablet, focusing after a tap
                  // would raise the on-screen keyboard.
                  focusOnMount={openedWithKeyboard}
                  placeholder={searchPlaceholder}
                  fullWidth
                />
              </Box>
            ) : null}

            <MenuList
              ref={listRef}
              // Opens onto the selected row, or the first.
              autoFocusItem={!searchable}
              sx={{ maxHeight: 320, overflowY: 'auto' }}
            >
              {visibleNullOption ? (
                <OptionRow
                  content={visibleNullOption}
                  selected={value === null}
                  showCheck={showSelectedCheck}
                  onSelect={() => {
                    pick(null)
                  }}
                />
              ) : null}
              {visibleOptions.map((option) => (
                <OptionRow
                  key={option.value}
                  content={option}
                  selected={option.value === value}
                  disabled={isOptionDisabled(option)}
                  showCheck={showSelectedCheck}
                  onSelect={() => {
                    // Re-picking the current option clears it — except when there's an explicit
                    // cleared row, which makes the panel read as a radio group where re-confirming
                    // your own choice landing you somewhere else would be a surprise, or when the
                    // select isn't clearable at all.
                    pick(
                      option.value === value && !nullOption && clearable
                        ? null
                        : option.value
                    )
                  }}
                />
              ))}
              {visibleOptions.length === 0 && !visibleNullOption ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 2, py: 1 }}
                >
                  No matches
                </Typography>
              ) : null}
            </MenuList>
          </>
        )
      }}
    </FilterDropdown>
  )
}

function OptionRow({
  content,
  selected,
  disabled = false,
  showCheck,
  onSelect,
  ...menuListProps
}: {
  content: RowContent
  selected: boolean
  disabled?: boolean
  showCheck: boolean
  onSelect: () => void
  // MenuList injects these into the row it opens onto.
  autoFocus?: boolean
  tabIndex?: number
}) {
  return (
    <MenuItem
      {...menuListProps}
      dense
      sx={{
        py: 0.75,
        px: 2,
        gap: 1,
        minHeight: 0,
        // The chosen row gets the same light-teal wash as an active
        // FilterDropdown chip, instead of MUI's default grey select.
        '&.Mui-selected': {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
          },
        },
      }}
      role="menuitemradio"
      aria-checked={selected}
      selected={selected}
      disabled={disabled}
      onClick={onSelect}
    >
      <ListItemText
        primary={content.label}
        secondary={content.subLabel}
        slotProps={{
          primary: { variant: 'body2' },
          secondary: { variant: 'caption' },
        }}
      />
      {content.count !== undefined ? (
        <Typography variant="body2" color="text.secondary">
          {content.count}
        </Typography>
      ) : null}
      {showCheck && selected ? (
        // 18px, matching the row's two-line height rather than the body text.
        <CheckIcon sx={{ fontSize: '1.125rem', color: 'primary.main' }} />
      ) : null}
    </MenuItem>
  )
}
